import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Home, 
  TrendingUp, 
  Download, 
  FileText, 
  BarChart3, 
  PieChart as PieChartIcon,
  Calendar,
  Users,
  Target,
  Activity,
  Clock,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend, AreaChart, Area } from 'recharts';
import { toast } from 'sonner';

// Mock data generators for reports
const generateAuditorPerformanceData = () => {
  const auditors = ['John Smith', 'Sarah Johnson', 'Mike Chen', 'Lisa Davis', 'Dr. Brown'];
  return auditors.map(name => ({
    auditorName: name,
    auditsCompleted: Math.floor(Math.random() * 100) + 50,
    averageTime: (Math.random() * 3 + 1).toFixed(1),
    overrideRate: (Math.random() * 15).toFixed(1),
    accuracy: (Math.random() * 10 + 90).toFixed(1),
    efficiency: Math.floor(Math.random() * 30) + 70,
  }));
};

const generateOperationalMetricsData = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  return months.map(month => ({
    month,
    totalAudits: Math.floor(Math.random() * 200) + 150,
    avgProcessingTime: Math.random() * 2 + 1,
    throughput: Math.floor(Math.random() * 50) + 25,
    systemUptime: Math.random() * 5 + 95,
  }));
};

const generateQualityMetricsData = () => {
  const departments = ['Cardiology', 'Orthopedics', 'Pediatrics', 'Emergency', 'Surgery'];
  return departments.map(dept => ({
    department: dept,
    complianceRate: Math.floor(Math.random() * 20) + 80,
    qualityScore: Math.floor(Math.random() * 15) + 85,
    deficiencyCount: Math.floor(Math.random() * 25) + 5,
    improvementTrend: Math.random() > 0.5 ? 'up' : 'down',
  }));
};

const generateClinicianPerformanceData = () => {
  const clinicians = ['Dr. Anderson', 'Dr. Rodriguez', 'NP Williams', 'Dr. Taylor', 'PA Johnson', 'Dr. Chen', 'NP Davis'];
  return clinicians.map(name => {
    const totalCharts = Math.floor(Math.random() * 80) + 40; // 40-120 total charts
    const complianceRate = Math.random() * 0.3 + 0.7; // 70-100% compliance rate
    const compliantCharts = Math.floor(totalCharts * complianceRate);
    const deficientCharts = totalCharts - compliantCharts;
    
    return {
      clinicianName: name,
      compliantCharts,
      deficientCharts,
      totalCharts,
      complianceRate: (complianceRate * 100).toFixed(1),
      documentationQuality: Math.floor(Math.random() * 20) + 80, // 80-100% quality
      treatmentAdherence: Math.floor(Math.random() * 15) + 85, // 85-100% adherence
      outcomeScore: Math.floor(Math.random() * 30) + 70, // 70-100 outcome score
    };
  });
};

const generateClinicianSummaryData = () => {
  const clinicians = ['Dr. Anderson', 'Dr. Rodriguez', 'NP Williams', 'Dr. Taylor', 'PA Johnson', 'Dr. Chen', 'NP Davis'];
  return clinicians.map(name => ({
    clinicianName: name,
    totalAudits: Math.floor(Math.random() * 80) + 20,
    complianceRate: (Math.random() * 20 + 80).toFixed(1),
    deficiencyCount: Math.floor(Math.random() * 15),
    avgConfidence: (Math.random() * 20 + 75).toFixed(1),
    riskLevel: ['Low', 'Medium', 'High'][Math.floor(Math.random() * 3)],
  }));
};

const generateDeficiencyTrendsData = () => {
  const goals = [
    'Documentation Quality', 
    'Treatment Planning', 
    'Follow-up Scheduling', 
    'Patient Education', 
    'Care Coordination'
  ];
  
  return goals.map(goal => ({
    goalType: goal,
    occurrences: Math.floor(Math.random() * 25) + 5,
    percentage: (Math.random() * 15 + 5).toFixed(1),
    trend: Math.random() > 0.5 ? 'increasing' : 'decreasing',
  }));
};

const generateTimeSeriesData = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  return months.map(month => ({
    month,
    audits: Math.floor(Math.random() * 200) + 100,
    compliance: Math.floor(Math.random() * 20) + 80,
    avgTime: Math.random() * 2 + 2,
    overrides: Math.floor(Math.random() * 20) + 5,
  }));
};

export function Reports() {
  const [reportType, setReportType] = useState('operational');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
    to: new Date(),
  });
  const [selectedClinician, setSelectedClinician] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const auditorPerformanceData = useMemo(() => generateAuditorPerformanceData(), []);
  const operationalMetricsData = useMemo(() => generateOperationalMetricsData(), []);
  const qualityMetricsData = useMemo(() => generateQualityMetricsData(), []);
  const clinicianPerformanceData = useMemo(() => generateClinicianPerformanceData(), []);
  const clinicianSummaryData = useMemo(() => generateClinicianSummaryData(), []);
  const deficiencyTrendsData = useMemo(() => generateDeficiencyTrendsData(), []);
  const timeSeriesData = useMemo(() => generateTimeSeriesData(), []);

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    
    // Simulate report generation
    setTimeout(() => {
      setIsGenerating(false);
      toast.success('Report generated successfully');
    }, 2000);
  };

  const handleExportCSV = () => {
    toast.success('Report exported to CSV');
  };

  const handleExportPDF = () => {
    toast.success('Report exported to PDF');
  };

  const getRiskLevelBadge = (level: string) => {
    const colorMap = {
      'Low': 'bg-green-100 text-green-800 border-green-200',
      'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'High': 'bg-red-100 text-red-800 border-red-200'
    };
    
    return (
      <Badge variant="outline" className={colorMap[level as keyof typeof colorMap]}>
        {level}
      </Badge>
    );
  };

  const pieChartData = deficiencyTrendsData.map((item, index) => ({
    name: item.goalType,
    value: item.occurrences,
    color: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index]
  }));

  // Report type specific chart configurations
  const renderChartsForReportType = () => {
    switch (reportType) {
      case 'operational':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Operational Metrics Time Series */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                  <Activity className="w-4 h-4" />
                  Operational Performance Trends
                </CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  System throughput and processing efficiency over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={operationalMetricsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Legend />
                      <Area type="monotone" dataKey="totalAudits" stackId="1" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.6} name="Total Audits" />
                      <Area type="monotone" dataKey="throughput" stackId="2" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name="Throughput Rate" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Processing Time Chart */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Processing Efficiency</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Average processing time per audit
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={operationalMetricsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Line type="monotone" dataKey="avgProcessingTime" stroke="#F59E0B" strokeWidth={2} name="Avg Time (hrs)" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* System Uptime */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>System Reliability</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  System uptime percentage
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={operationalMetricsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={9.6} />
                      <YAxis domain={[90, 100]} stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Line type="monotone" dataKey="systemUptime" stroke="#059669" strokeWidth={2} name="Uptime %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'quality':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quality Metrics by Department */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                  <CheckCircle className="w-4 h-4" />
                  Quality Metrics by Department
                </CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Compliance rates and quality scores across departments
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={qualityMetricsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="department" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Legend />
                      <Bar dataKey="complianceRate" fill="#10B981" name="Compliance Rate %" />
                      <Bar dataKey="qualityScore" fill="#3B82F6" name="Quality Score %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Deficiency Distribution */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Deficiency Types</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Most common deficiency categories
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Quality Improvement Trends */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Improvement Trends</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Quality improvement over time
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={qualityMetricsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="department" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Bar dataKey="deficiencyCount" fill="#EF4444" name="Deficiency Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'auditor-performance':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Auditor Efficiency */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                  <Users className="w-4 h-4" />
                  Auditor Performance Metrics
                </CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Productivity and accuracy metrics by auditor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={auditorPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="auditorName" stroke="#6B7280" fontSize={9.6} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Legend />
                      <Bar dataKey="auditsCompleted" fill="#3B82F6" name="Audits Completed" />
                      <Bar dataKey="efficiency" fill="#10B981" name="Efficiency Score" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Accuracy Metrics */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Accuracy Rates</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Audit accuracy by auditor
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={auditorPerformanceData} layout="horizontal">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" domain={[80, 100]} stroke="#6B7280" fontSize={9.6} />
                      <YAxis type="category" dataKey="auditorName" stroke="#6B7280" fontSize={9.6} width={80} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Bar dataKey="accuracy" fill="#059669" name="Accuracy %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Processing Time */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Processing Time</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Average time per audit
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={auditorPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="auditorName" stroke="#6B7280" fontSize={9.6} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Bar dataKey="averageTime" fill="#F59E0B" name="Avg Time (hrs)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'clinician-summary':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Clinician Performance - Stacked Bar Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                  <Target className="w-4 h-4" />
                  Clinician Performance
                </CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Compliant vs deficient charts by clinician
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={clinicianPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="clinicianName" 
                        stroke="#6B7280" 
                        fontSize={9.6}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip 
                        contentStyle={{ fontSize: '11.2px' }}
                        formatter={(value, name) => [value, name === 'compliantCharts' ? 'Compliant Charts' : 'Deficient Charts']}
                      />
                      <Legend />
                      <Bar dataKey="compliantCharts" stackId="a" fill="#10B981" name="Compliant Charts" />
                      <Bar dataKey="deficientCharts" stackId="a" fill="#EF4444" name="Deficient Charts" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Risk Assessment */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Risk Assessment</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Clinician risk distribution
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Low Risk', value: clinicianSummaryData.filter(c => c.riskLevel === 'Low').length, color: '#10B981' },
                          { name: 'Medium Risk', value: clinicianSummaryData.filter(c => c.riskLevel === 'Medium').length, color: '#F59E0B' },
                          { name: 'High Risk', value: clinicianSummaryData.filter(c => c.riskLevel === 'High').length, color: '#EF4444' }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {[
                          { color: '#10B981' },
                          { color: '#F59E0B' },
                          { color: '#EF4444' }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Trends */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Compliance Rates</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Overall compliance by clinician
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={clinicianPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="clinicianName" stroke="#6B7280" fontSize={9.6} angle={-45} textAnchor="end" height={80} />
                      <YAxis domain={[0, 100]} stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Bar dataKey="complianceRate" fill="#3B82F6" name="Compliance Rate %" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'deficiency-trends':
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Deficiency Trends Over Time */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                  <AlertTriangle className="w-4 h-4" />
                  Deficiency Trends Analysis
                </CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Tracking deficiency patterns and improvement trends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Legend />
                      <Line type="monotone" dataKey="overrides" stroke="#EF4444" strokeWidth={2} name="Override Count" />
                      <Line type="monotone" dataKey="compliance" stroke="#10B981" strokeWidth={2} name="Compliance Rate %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Deficiency Distribution */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Deficiency Categories</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Most common deficiency types
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Deficiency Impact */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Impact Analysis</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Deficiency frequency and impact
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={deficiencyTrendsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="goalType" stroke="#6B7280" fontSize={9.6} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Bar dataKey="occurrences" fill="#F59E0B" name="Occurrences" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Default Time Series Chart */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Audit Volume Trends</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Monthly audit completion and compliance rates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="month" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Legend />
                      <Line 
                        type="monotone" 
                        dataKey="audits" 
                        stroke="#3B82F6" 
                        strokeWidth={2}
                        name="Audits Completed"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="compliance" 
                        stroke="#10B981" 
                        strokeWidth={2}
                        name="Compliance Rate (%)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Default Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Deficiency Distribution</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Most common areas of non-compliance
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Default Stacked Clinician Performance */}
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Clinician Performance</CardTitle>
                <CardDescription style={{ fontSize: '11.2px' }}>
                  Compliant vs deficient charts by clinician
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={clinicianPerformanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis 
                        dataKey="clinicianName" 
                        stroke="#6B7280" 
                        fontSize={9.6}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip 
                        contentStyle={{ fontSize: '11.2px' }}
                        formatter={(value, name) => [value, name === 'compliantCharts' ? 'Compliant Charts' : 'Deficient Charts']}
                      />
                      <Legend />
                      <Bar dataKey="compliantCharts" stackId="a" fill="#10B981" name="Compliant Charts" />
                      <Bar dataKey="deficientCharts" stackId="a" fill="#EF4444" name="Deficient Charts" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        {/* Breadcrumb Navigation */}
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-2" style={{ fontSize: '11.2px' }}>
                  <TrendingUp className="w-3 h-3" />
                  Reports & Analytics
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Header */}
        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                Reports & Analytics
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Generate comprehensive reports and analyze audit performance
              </p>
            </div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Report Configuration */}
        <div className="px-8 pb-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontSize: '14px' }}>
                <FileText className="w-4 h-4" />
                Report Configuration
              </CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Select criteria and filters for your report
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                {/* Report Type */}
                <div className="space-y-2">
                  <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Report Type</Label>
                  <Select value={reportType} onValueChange={setReportType}>
                    <SelectTrigger style={{ fontSize: '11.2px' }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operational">Operational Metrics</SelectItem>
                      <SelectItem value="quality">Quality Metrics</SelectItem>
                      <SelectItem value="auditor-performance">Auditor Performance</SelectItem>
                      <SelectItem value="clinician-summary">Clinician Summary</SelectItem>
                      <SelectItem value="deficiency-trends">Deficiency Trends</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Clinician Filter */}
                <div className="space-y-2">
                  <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Clinician (Optional)</Label>
                  <Input
                    placeholder="Filter by clinician..."
                    value={selectedClinician}
                    onChange={(e) => setSelectedClinician(e.target.value)}
                    style={{ fontSize: '11.2px' }}
                  />
                </div>

                {/* Date Range */}
                <div className="space-y-2">
                  <Label style={{ fontSize: '11.2px', fontWeight: 500 }}>Date Range</Label>
                  <DateRangeFilter
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  onClick={handleGenerateReport}
                  disabled={isGenerating}
                  style={{ fontSize: '11.2px' }}
                >
                  {isGenerating ? (
                    <>
                      <Activity className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
                
                <Button variant="outline" onClick={handleExportCSV} style={{ fontSize: '11.2px' }}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                
                <Button variant="outline" onClick={handleExportPDF} style={{ fontSize: '11.2px' }}>
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Results */}
        <div className="flex-1 px-8 pb-8">
          <Tabs defaultValue="charts" className="h-full">
            <TabsList className="mb-6">
              <TabsTrigger value="charts" className="flex items-center gap-2" style={{ fontSize: '10px' }}>
                <BarChart3 className="w-4 h-4" />
                Charts & Visualizations
              </TabsTrigger>
              <TabsTrigger value="tables" className="flex items-center gap-2" style={{ fontSize: '10px' }}>
                <FileText className="w-4 h-4" />
                Data Tables
              </TabsTrigger>
              <TabsTrigger value="summary" className="flex items-center gap-2" style={{ fontSize: '10px' }}>
                <Target className="w-4 h-4" />
                Executive Summary
              </TabsTrigger>
            </TabsList>

            <TabsContent value="charts" className="space-y-6">
              {renderChartsForReportType()}
            </TabsContent>

            <TabsContent value="tables" className="space-y-6">
              {/* Clinician Performance Table */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '14px' }}>Clinician Performance Metrics</CardTitle>
                  <CardDescription style={{ fontSize: '11.2px' }}>
                    Detailed performance metrics for each clinician
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead style={{ fontSize: '11.2px' }}>Clinician</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Total Charts</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Compliant Charts</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Deficient Charts</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Compliance Rate (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Documentation Quality (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Treatment Adherence (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Outcome Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clinicianPerformanceData.map((clinician, index) => (
                        <TableRow key={index}>
                          <TableCell style={{ fontSize: '11.2px', fontWeight: 500 }}>
                            {clinician.clinicianName}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {clinician.totalCharts}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className="text-green-600">
                              {clinician.compliantCharts}
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className="text-red-600">
                              {clinician.deficientCharts}
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={parseFloat(clinician.complianceRate) >= 90 ? 'text-green-600' : parseFloat(clinician.complianceRate) >= 80 ? 'text-yellow-600' : 'text-red-600'}>
                              {clinician.complianceRate}%
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={clinician.documentationQuality >= 90 ? 'text-green-600' : 'text-yellow-600'}>
                              {clinician.documentationQuality}%
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={clinician.treatmentAdherence >= 90 ? 'text-green-600' : 'text-yellow-600'}>
                              {clinician.treatmentAdherence}%
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={clinician.outcomeScore >= 85 ? 'text-green-600' : clinician.outcomeScore >= 75 ? 'text-yellow-600' : 'text-red-600'}>
                              {clinician.outcomeScore}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Auditor Performance Table */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '14px' }}>Auditor Performance Summary</CardTitle>
                  <CardDescription style={{ fontSize: '11.2px' }}>
                    Detailed metrics for each auditor
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead style={{ fontSize: '11.2px' }}>Auditor</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Audits Completed</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Avg Time (hrs)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Override Rate (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Accuracy (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Efficiency Score</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditorPerformanceData.map((auditor, index) => (
                        <TableRow key={index}>
                          <TableCell style={{ fontSize: '11.2px', fontWeight: 500 }}>
                            {auditor.auditorName}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {auditor.auditsCompleted}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {auditor.averageTime}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={parseFloat(auditor.overrideRate) > 10 ? 'text-red-600' : 'text-green-600'}>
                              {auditor.overrideRate}%
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={parseFloat(auditor.accuracy) > 95 ? 'text-green-600' : 'text-yellow-600'}>
                              {auditor.accuracy}%
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {auditor.efficiency}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Clinician Summary Table */}
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '14px' }}>Clinician Compliance Summary</CardTitle>
                  <CardDescription style={{ fontSize: '11.2px' }}>
                    Compliance rates and risk assessment by clinician
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead style={{ fontSize: '11.2px' }}>Clinician</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Total Audits</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Compliance Rate (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Deficiencies</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Avg Confidence (%)</TableHead>
                        <TableHead style={{ fontSize: '11.2px' }}>Risk Level</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clinicianSummaryData.map((clinician, index) => (
                        <TableRow key={index}>
                          <TableCell style={{ fontSize: '11.2px', fontWeight: 500 }}>
                            {clinician.clinicianName}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {clinician.totalAudits}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            <span className={parseFloat(clinician.complianceRate) > 90 ? 'text-green-600' : 'text-yellow-600'}>
                              {clinician.complianceRate}%
                            </span>
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {clinician.deficiencyCount}
                          </TableCell>
                          <TableCell style={{ fontSize: '11.2px' }}>
                            {clinician.avgConfidence}%
                          </TableCell>
                          <TableCell>
                            {getRiskLevelBadge(clinician.riskLevel || 'Low')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="summary" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontSize: '14px' }}>Executive Summary</CardTitle>
                  <CardDescription style={{ fontSize: '11.2px' }}>
                    Key insights and recommendations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="text-green-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                        Overall Performance
                      </h4>
                      <p className="text-green-800" style={{ fontSize: '10px' }}>
                        System is performing well with {auditorPerformanceData.reduce((sum, a) => sum + a.auditsCompleted, 0)} audits completed 
                        and an average accuracy of {(auditorPerformanceData.reduce((sum, a) => sum + parseFloat(a.accuracy), 0) / auditorPerformanceData.length).toFixed(1)}%.
                      </p>
                    </div>
                    
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <h4 className="text-yellow-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                        Areas for Improvement
                      </h4>
                      <p className="text-yellow-800" style={{ fontSize: '10px' }}>
                        Documentation Quality shows the highest deficiency rate. Consider additional training 
                        for clinicians and updated documentation templates.
                      </p>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h4 className="text-blue-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                        Recommendations
                      </h4>
                      <p className="text-blue-800" style={{ fontSize: '10px' }}>
                        Implement targeted training programs for high-risk clinicians and consider 
                        adjusting AI confidence thresholds based on performance data.
                      </p>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg">
                    <h4 className="text-gray-900 mb-3" style={{ fontSize: '12px', fontWeight: 600 }}>
                      Key Performance Indicators
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                          {auditorPerformanceData.reduce((sum, a) => sum + a.auditsCompleted, 0)}
                        </p>
                        <p className="text-gray-600" style={{ fontSize: '10px' }}>Total Audits</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                          {(auditorPerformanceData.reduce((sum, a) => sum + parseFloat(a.accuracy), 0) / auditorPerformanceData.length).toFixed(1)}%
                        </p>
                        <p className="text-gray-600" style={{ fontSize: '10px' }}>Avg Accuracy</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                          {(auditorPerformanceData.reduce((sum, a) => sum + parseFloat(a.overrideRate), 0) / auditorPerformanceData.length).toFixed(1)}%
                        </p>
                        <p className="text-gray-600" style={{ fontSize: '10px' }}>Override Rate</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                          {clinicianSummaryData.filter(c => c.riskLevel === 'High').length}
                        </p>
                        <p className="text-gray-600" style={{ fontSize: '10px' }}>High Risk Clinicians</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}